/* eslint-disable @typescript-eslint/no-explicit-any */
 
import React, { useState, useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import styles from "./ChartOfAccounts.module.css";
import { useNavigate } from "react-router-dom";

import { Search, Add, ArrowBack } from "@mui/icons-material";

import PlanOfAccountsList from "./ChartOfAccountsList";
import AddPlanOfAccountsModal from "./AddChartOfAccountsModal/AddChartOfAccountsModal";
import {
    loadChartOfAccounts,
    type IChartOfAccountsFilter,
    type IPlanoContas,
    toggleChartOfAccountsStatus,
    cancelChartOfAccounts
} from "../../../../services/financial";
import Button from "../../../../components/Button/Button";
import InputSearch from "../../../../components/InputSearch/InputSearch";

const ChartOfAccounts = () => {
    const navigate = useNavigate();

    const [parentEntryToAdd, setParentEntryToAdd] = useState<IPlanoContas | undefined>(undefined);
    const [entryToEdit, setEntryToEdit] = useState<IPlanoContas | undefined>(undefined);
    const [isHidden, setIsHidden] = useState<{ addEntry?: boolean }>({ addEntry: true });

    const [search, setSearch] = useState("");
    const [filterValues, setFilterValues] = useState<IChartOfAccountsFilter>({ tipo: 'ambos' });

    const {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        refetch
    } = useInfiniteQuery({
        queryKey: ["planoContas", search, filterValues],
        initialPageParam: 1,
        queryFn: ({ pageParam = 1 }) => loadChartOfAccounts(search, pageParam as number, filterValues),
        getNextPageParam: (lastPage, allPages) => {
            const currentTotalLoaded = allPages.reduce((acc, page) => acc + page.rows.length, 0);
            if (currentTotalLoaded < lastPage.count) {
                return allPages.length + 1;
            }
            return undefined;
        },
        refetchOnWindowFocus: false,
    });


    const allRows = useMemo(() => {
        return data?.pages.flatMap((page) => page.rows) || [];
    }, [data]);

    const stats = useMemo(() => {
        const lastPage = data?.pages[data.pages.length - 1];
        return lastPage?.stats || { ativos: 0, inativos: 0, total: 0 };
    }, [data]);

    const handleCardClick = (statusType: 'ativos' | 'inativos' | 'total') => {
        setFilterValues((prev) => {
            if (statusType === 'ativos' && prev.ativo === true) {
                const { ativo, ...rest } = prev;
                return rest;
            }
            if (statusType === 'inativos' && prev.ativo === false) {
                const { ativo, ...rest } = prev;
                return rest;
            }
            if (statusType === 'total') {
                const { ativo, ...rest } = prev;
                return rest;
            }
            return {
                ...prev,
                ativo: statusType === 'ativos' ? true : false
            };
        });
    };

    const handleTypeFilter = (tipo: 'receita' | 'despesa' | 'ambos') => {
        setFilterValues(prev => ({ ...prev, tipo }));
    };

    const handleAddChild = (parentItem: IPlanoContas) => {
        setEntryToEdit(undefined);
        setParentEntryToAdd(parentItem);
        setIsHidden((prev) => ({ ...prev, addEntry: false }));
    };

    const handleOpenModalAdd = () => {
        setEntryToEdit(undefined);
        setParentEntryToAdd(undefined);
        setIsHidden((prev) => ({ ...prev, addEntry: false }));
    };

    const handleCloseModalAdd = () => {
        setEntryToEdit(undefined);
        setParentEntryToAdd(undefined);
        setIsHidden((prev) => ({ ...prev, addEntry: true }));
        refetch();
    };

    const handleEditItem = (item: IPlanoContas) => {
        setEntryToEdit(item);
        setParentEntryToAdd(undefined);
        setIsHidden((prev) => ({ ...prev, addEntry: false }));
    };

    const handleToggleItemStatus = async (item: IPlanoContas) => {
        try {
            await toggleChartOfAccountsStatus(item.uid_plano_contas);
            refetch();
        } catch (error: any) {
            console.error(error);
            const msg = error?.message || "Erro ao alterar o status da conta.";
            alert(msg);
        }
    };

    const handleDeleteItem = async (uid: string) => {
        const confirm = window.confirm("Deseja realmente excluir esta conta? Esta ação não pode ser desfeita.");
        if (!confirm) return;

        try {
            await cancelChartOfAccounts(uid);
            refetch();
            alert("Conta excluída com sucesso!");
        } catch (error: any) {
            const msg = error?.message || "Erro ao excluir conta";
            alert(msg);
            console.error(error);
        }
    };

    const totalCount = data?.pages[0]?.count || 0;

    return (
        <div className={styles.containerProjects}>
            <div className={styles.purchases}>
                <div className={styles.header}>
                    <h1>Plano de Contas</h1>

                    <InputSearch
                        searchValue={search}
                        setSearchValue={setSearch}
                        placeholder="Pesquisar por nome ou código"
                    />

                    <div className={styles.headerButtons}>
                        <p>Qtd: <span>{totalCount}</span></p>
                        <Button
                            variant="contain"
                            onClick={handleOpenModalAdd}
                            title="Adicionar conta"
                        >
                            <Add />
                        </Button>
                        <Button
                            variant="contain"
                            onClick={() => navigate(-1)}
                            title="Voltar para página anterior"
                        >
                            <ArrowBack />
                        </Button>
                    </div>
                </div>

                <div className={styles.financialStatsCard}>
                    <div className={styles.statLine}>
                        <div
                            className={`${styles.statItem} ${styles.ativosCard} ${filterValues.ativo === true ? styles.activeFilter : ''}`}
                            onClick={() => handleCardClick('ativos')}
                        >
                            <span className={styles.statText}>Ativos</span>
                            <span className={styles.statCount}>
                                {isLoading ? "..." : stats.ativos}
                            </span>
                        </div>

                        <div
                            className={`${styles.statItem} ${styles.inativosCard} ${filterValues.ativo === false ? styles.activeFilter : ''}`}
                            onClick={() => handleCardClick('inativos')}
                        >
                            <span className={styles.statText}>Inativos</span>
                            <span className={styles.statCount}>
                                {isLoading ? "..." : stats.inativos}
                            </span>
                        </div>

                        <div className={`${styles.statItem} ${styles.totalCard}`} onClick={() => handleCardClick('total')}>
                            <span className={styles.statText}>Total</span>
                            <span className={styles.statCount}>
                                {isLoading ? "..." : stats.total}
                            </span>
                        </div>
                    </div>
                </div>

                <div className={styles.typeFilters}>
                    <button
                        className={!filterValues.tipo || filterValues.tipo === 'ambos' ? styles.activeType : ''}
                        onClick={() => handleTypeFilter('ambos')}
                    >Todos</button>
                    <button
                        className={filterValues.tipo === 'receita' ? styles.activeType : ''}
                        onClick={() => handleTypeFilter('receita')}
                    >Receitas</button>
                    <button
                        className={filterValues.tipo === 'despesa' ? styles.activeType : ''}
                        onClick={() => handleTypeFilter('despesa')}
                    >Despesas</button>
                </div>

                <PlanOfAccountsList
                    rows={allRows}
                    handleEdit={handleEditItem}
                    handleAddChild={handleAddChild}
                    handleToggleStatus={handleToggleItemStatus}
                    handleDelete={handleDeleteItem}
                />

                <div className={styles.loadMore}>
                    {hasNextPage ? (
                        <Button
                            variant="contain"
                            onClick={() => fetchNextPage()}
                            disabled={isFetchingNextPage || isFetching}
                        >
                            {isFetchingNextPage ? "Carregando..." : "Carregar Mais"}
                        </Button>
                    ) : allRows.length === 0 && !isLoading ? (
                        <p className={styles.tableLastItem}>Nenhum registro encontrado</p>
                    ) : allRows.length > 0 ? (
                        <p className={styles.tableLastItem}>Todos os registros carregados</p>
                    ) : null}
                </div>
            </div>

            {!isHidden.addEntry && (
                <AddPlanOfAccountsModal
                    onClose={handleCloseModalAdd}
                    entryToEdit={entryToEdit}
                    parentEntry={parentEntryToAdd}
                    refetch={refetch}
                />
            )}
        </div>
    );
};

export default ChartOfAccounts;